import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { format, addDays, startOfWeek, startOfMonth, endOfMonth } from 'date-fns';
import { MapContainer, TileLayer, Circle, CircleMarker, Popup } from 'react-leaflet';
import { X, ChevronLeft, ChevronRight, Search } from 'lucide-react';
import { toast } from 'sonner';
import api from '../services/api';
import { API } from '../App';
import DashboardButton from '../components/DashboardButton';
import DashboardDropdown from '../components/DashboardDropdown';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import 'leaflet/dist/leaflet.css';

const Areas = ({ user }) => {
  console.log('🚨 AREAS PAGE COMPONENT LOADED - VERSION 2.0 WITH UPDATES');
  const navigate = useNavigate();
  const [viewMode, setViewMode] = useState('day'); // 'day', 'week', 'month'
  const [selectedDate, setSelectedDate] = useState(addDays(new Date(), 1)); // Default to tomorrow
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [showCalendarModal, setShowCalendarModal] = useState(false);
  const [settings, setSettings] = useState(null);
  const [vehicles, setVehicles] = useState([]);
  const [vehicleColors, setVehicleColors] = useState({});
  const [baseLocation, setBaseLocation] = useState(null);
  const [areaData, setAreaData] = useState([]);
  const [postcodeLocations, setPostcodeLocations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isDashboardOpen, setIsDashboardOpen] = useState(false);
  const [showSearchBar, setShowSearchBar] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [isSearching, setIsSearching] = useState(false);
  const searchBarRef = useRef(null);
  const searchTimeoutRef = useRef(null);

  const enconOrange = '#ee7203';
  const enconBlack = '#1d1d1b';
  const enconGrey = '#9d9d9c';

  // Debounced search function
  const debouncedSearch = useCallback((query) => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    searchTimeoutRef.current = setTimeout(() => {
      handleInlineSearch(query);
    }, 300);
  }, []);

  // Search function
  const handleInlineSearch = async (query) => {
    const searchTerm = query.trim();
    if (!searchTerm) {
      setSearchResults(null);
      setIsSearching(false);
      return;
    }

    setIsSearching(true);

    try {
      const endDate = new Date();
      const startDate = new Date();
      startDate.setMonth(startDate.getMonth() - 3);

      const response = await api.post(`${API}/search`, {
        query: searchTerm,
        search_type: 'all',
        start_date: format(startDate, 'yyyy-MM-dd'),
        end_date: format(endDate, 'yyyy-MM-dd')
      });

      if (response.data.results && response.data.results.length > 0) {
        setSearchResults({ 
          found: true, 
          results: response.data.results, 
          count: response.data.results.length 
        });
        toast.success(`Found ${response.data.results.length} order(s)`, { duration: 2000 });
      } else {
        setSearchResults({ found: false, message: 'No matching orders found' });
      }
    } catch (error) {
      console.error('Search error:', error);
      setSearchResults({ found: false, message: 'Search failed' });
      toast.error('Search failed', { duration: 2000 });
    } finally {
      setIsSearching(false);
    }
  };

  // Vehicle color mapping - use colors from vehicle settings
  const getVehicleColor = (vehicleReg, vehicleType) => {
    // First check if we have a color from vehicle settings
    if (vehicleColors[vehicleReg]) {
      return vehicleColors[vehicleReg];
    }
    
    // Fallback to default colors if not found in settings
    const defaultVehicleColors = {
      'VX24 ZBW': '#ee7203', // orange (truck 1)
      'VX24 YXK': '#ef4444', // red (truck 2)
      'VX24 ZDT': '#eab308', // yellow (truck 3)
      'PO23 XYD': '#a855f7', // purple (van)
    };

    // If vehicle is in default list, use its assigned color
    if (defaultVehicleColors[vehicleReg]) {
      return defaultVehicleColors[vehicleReg];
    }

    // For additional vehicles not in defaults, cycle through additional colors
    const additionalColors = ['#3b82f6', '#22c55e', '#92400e']; // blue, green, brown
    const hash = vehicleReg.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
    return additionalColors[hash % additionalColors.length];
  };

  // Load settings and vehicles on mount
  useEffect(() => {
    loadSettings();
    loadVehicles();
    // Debug indicator - remove after testing
    toast.info('Areas page loaded - v2.0 with cost/contribution', { duration: 3000 });
  }, []);

  const loadSettings = async () => {
    try {
      const response = await api.get(`${API}/settings`);
      console.log('Settings loaded:', response.data);
      setSettings(response.data);
      
      // Get base postcode location - check multiple possible field names
      const basePostcode = response.data.base_postcode || response.data.basePostcode;
      console.log('Base postcode from settings:', basePostcode);
      
      if (basePostcode) {
        const location = await fetchPostcodeLocation(basePostcode);
        console.log('Base location:', location);
        setBaseLocation(location);
      } else {
        console.error('No base postcode found in settings');
      }
    } catch (error) {
      console.error('Error loading settings:', error);
    }
  };

  const loadVehicles = async () => {
    try {
      const response = await api.get(`${API}/vehicles`);
      setVehicles(response.data);
      
      // Create color mapping
      const colorMap = {};
      response.data.forEach(v => {
        if (v.vehicle_registration && v.color_code) {
          colorMap[v.vehicle_registration] = v.color_code;
        }
      });
      setVehicleColors(colorMap);
    } catch (error) {
      console.error('Error loading vehicles:', error);
    }
  };

  // Fetch postcode location via backend proxy
  const fetchPostcodeLocation = async (postcode) => {
    try {
      const cleanPostcode = postcode.trim().replace(/\s+/g, '');
      console.log(`Fetching location for postcode: ${postcode} (cleaned: ${cleanPostcode})`);
      
      const response = await api.get(`${API}/postcode/${cleanPostcode}`);
      
      console.log(`Postcode API response for ${postcode}:`, response.data);
      
      if (response.data) {
        return response.data;
      }
    } catch (error) {
      console.error(`Error fetching postcode ${postcode}:`, error.response?.data || error.message);
    }
    return null;
  };

  // Load area data based on view mode and date
  useEffect(() => {
    if (settings) {
      loadAreaData();
    }
  }, [selectedDate, viewMode, settings]);

  const loadAreaData = async () => {
    setLoading(true);
    try {
      let dates = [];
      
      if (viewMode === 'day') {
        dates = [format(selectedDate, 'yyyy-MM-dd')];
      } else if (viewMode === 'week') {
        const weekStart = startOfWeek(selectedDate, { weekStartsOn: 1 });
        dates = Array.from({ length: 5 }, (_, i) => format(addDays(weekStart, i), 'yyyy-MM-dd'));
      } else if (viewMode === 'month') {
        const monthStart = startOfMonth(selectedDate);
        const monthEnd = endOfMonth(selectedDate);
        const daysInMonth = Math.floor((monthEnd - monthStart) / (1000 * 60 * 60 * 24)) + 1;
        dates = Array.from({ length: daysInMonth }, (_, i) => 
          format(addDays(monthStart, i), 'yyyy-MM-dd')
        ).filter(date => {
          const day = new Date(date).getDay();
          return day !== 0 && day !== 6; // Exclude weekends
        });
      }

      console.log(`Loading data for dates:`, dates);

      // Fetch data for all dates
      // Map structure: vehicle_reg -> { vehicle info + areas with times }
      const vehicleMap = new Map();
      const allPostcodes = [];
      let totalEntriesFound = 0;

      for (const date of dates) {
        try {
          console.log(`Fetching data for ${date}...`);
          const response = await api.get(`${API}/logs/${date}`);
          console.log(`Response for ${date}:`, response.data);
          
          if (response.data && response.data.vehicles) {
            console.log(`Found ${response.data.vehicles.length} vehicles for ${date}`);
            
            for (const vehicle of response.data.vehicles) {
              const allEntries = [
                ...(vehicle.entries || []),
                ...(vehicle.runs || []).flatMap(run => run.entries || [])
              ];

              console.log(`Vehicle ${vehicle.vehicle_registration} has ${allEntries.length} entries`);
              totalEntriesFound += allEntries.length;

              // Initialize vehicle in map if not exists
              if (!vehicleMap.has(vehicle.vehicle_registration)) {
                vehicleMap.set(vehicle.vehicle_registration, {
                  vehicleReg: vehicle.vehicle_registration,
                  vehicleType: vehicle.vehicle_type,
                  isFull: vehicle.runs?.some(run => run.is_full) || vehicle.is_full || false,
                  isOffRoad: vehicle.off_road || false,
                  areaDetails: new Map() // Map of area -> array of times
                });
              }

              const vehicleData = vehicleMap.get(vehicle.vehicle_registration);

              // Update off road status
              if (vehicle.off_road) {
                vehicleData.isOffRoad = true;
              }

              for (const entry of allEntries) {
                if (entry.postcode) {
                  console.log(`Processing postcode: ${entry.postcode} for vehicle ${vehicle.vehicle_registration}`);
                  
                  // Fetch location for this postcode
                  const location = await fetchPostcodeLocation(entry.postcode);
                  if (location) {
                    console.log(`Got location for ${entry.postcode}:`, location);
                    
                    // Calculate cost per entry for this run
                    const runEntries = vehicle.entries || [];
                    const entriesCount = runEntries.length || 1;
                    const vehicleDailyCost = vehicle.driver_cost || 0;
                    const costPerEntry = vehicleDailyCost / entriesCount;
                    
                    allPostcodes.push({
                      ...location,
                      time: entry.time_slot,
                      orderNumber: entry.order_number,
                      customer: entry.customer_name,
                      vehicleReg: vehicle.vehicle_registration,
                      isFull: vehicle.runs?.some(run => run.is_full) || vehicle.is_full || false,
                      date: date,
                      contribution: entry.contribution || 0,
                      costPerEntry: costPerEntry
                    });

                    // Add area with time to vehicle's area details
                    if (!vehicleData.areaDetails.has(location.area)) {
                      vehicleData.areaDetails.set(location.area, []);
                    }
                    if (entry.time_slot) {
                      vehicleData.areaDetails.get(location.area).push(entry.time_slot);
                    }
                    
                    console.log(`Added area ${location.area} with time ${entry.time_slot} to vehicle ${vehicle.vehicle_registration}`);
                  } else {
                    console.warn(`No location found for postcode: ${entry.postcode}`);
                  }
                }
              }

              // Update isFull status
              if (vehicle.runs?.some(run => run.is_full) || vehicle.is_full) {
                vehicleData.isFull = true;
              }
            }
          } else {
            console.log(`No vehicle data found for ${date}`);
          }
        } catch (error) {
          console.error(`Error loading data for ${date}:`, error);
        }
      }

      console.log(`Total entries found: ${totalEntriesFound}`);
      console.log(`Total vehicles: ${vehicleMap.size}`);
      console.log(`Total postcode locations: ${allPostcodes.length}`);

      // Convert to array format for display
      const vehicleDataArray = Array.from(vehicleMap.values()).map(v => {
        // Convert areaDetails Map to sorted array
        const areas = Array.from(v.areaDetails.entries())
          .map(([area, times]) => ({
            name: area,
            times: times.sort()
          }))
          .sort((a, b) => a.name.localeCompare(b.name));

        return {
          ...v,
          areas,
          hasDeliveries: areas.length > 0
        };
      });

      // Sort: vehicles with deliveries first, off-road vehicles at the bottom
      vehicleDataArray.sort((a, b) => {
        if (a.isOffRoad && !b.isOffRoad) return 1;
        if (!a.isOffRoad && b.isOffRoad) return -1;
        if (!a.hasDeliveries && b.hasDeliveries) return 1;
        if (a.hasDeliveries && !b.hasDeliveries) return -1;
        return a.vehicleReg.localeCompare(b.vehicleReg);
      });

      setAreaData(vehicleDataArray);
      setPostcodeLocations(allPostcodes);
    } catch (error) {
      console.error('Error loading area data:', error);
    } finally {
      setLoading(false);
    }
  };

  const prevPeriod = () => {
    if (viewMode === 'day') {
      setSelectedDate(addDays(selectedDate, -1));
    } else if (viewMode === 'week') {
      setSelectedDate(addDays(selectedDate, -7));
    } else if (viewMode === 'month') {
      setSelectedDate(addDays(startOfMonth(selectedDate), -1));
    }
  };

  const nextPeriod = () => {
    if (viewMode === 'day') {
      setSelectedDate(addDays(selectedDate, 1));
    } else if (viewMode === 'week') {
      setSelectedDate(addDays(selectedDate, 7));
    } else if (viewMode === 'month') {
      setSelectedDate(addDays(endOfMonth(selectedDate), 1));
    }
  };

  const getDateDisplay = (includeYear = true) => {
    if (viewMode === 'day') {
      return format(selectedDate, includeYear ? 'EEEE dd MMMM yyyy' : 'EEEE dd MMMM');
    } else if (viewMode === 'week') {
      const weekStart = startOfWeek(selectedDate, { weekStartsOn: 1 });
      return `Week commencing ${format(weekStart, includeYear ? 'dd MMMM yyyy' : 'dd MMMM')}`;
    } else {
      return format(selectedDate, includeYear ? 'MMMM yyyy' : 'MMMM');
    }
  };

  return (
    <div className="h-screen flex flex-col bg-white areas-page-overlay">
      {/* Header - Static, doesn't scroll */}
      <div className="w-full bg-white border-b-2 p-2 z-50 md:hidden relative" style={{ borderColor: enconOrange, position: 'sticky', top: 0 }}>
        {/* Mobile: Clean header with Settings + Dashboard */}
        <div className="space-y-1.5">
          {/* Top row: Settings + Dashboard (centered) */}
          <div className="flex items-center justify-between gap-2 relative">
            {/* Left: Settings Button */}
            <button
              onClick={() => window.location.href = '/settings'}
              className="p-2 rounded-xl luxury-control hover:shadow-lg flex items-center justify-center"
              style={{ color: enconOrange }}
              title="Settings"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 616 0z" />
              </svg>
            </button>
            
            {/* Centered Dashboard Button */}
            <div className="absolute left-1/2 transform -translate-x-1/2">
              <DashboardButton 
                onToggle={() => setIsDashboardOpen(!isDashboardOpen)} 
                isOpen={isDashboardOpen} 
              />
            </div>
          </div>
          
          {/* Dashboard Dropdown for mobile */}
          <DashboardDropdown 
            isOpen={isDashboardOpen} 
            user={user} 
            onClose={() => setIsDashboardOpen(false)} 
          />

          {/* Date Input + Close Button - Same row */}
          <div className="flex items-center gap-2">
            {/* Date Display - Reduced width */}
            <input
              type="date"
              value={format(selectedDate, 'yyyy-MM-dd')}
              onChange={(e) => setSelectedDate(new Date(e.target.value))}
              className="flex-1 py-1 px-2 rounded-md font-semibold text-center text-sm border-2"
              style={{ borderColor: enconOrange, color: enconBlack }}
            />
            
            {/* Close Button - Next to date */}
            <button
              onClick={() => navigate(-1)}
              className="p-1.5 rounded-full bg-white shadow-md hover:shadow-lg transition-all flex-shrink-0"
              style={{ color: enconOrange }}
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      {/* View Selector Bar - Slim grey bar under header */}
      <div 
        className="w-full py-2 px-4 lg:px-8 border-b sticky top-0 md:top-auto z-40"
        style={{ 
          backgroundColor: 'rgba(128, 128, 128, 0.1)',
          borderBottomColor: '#e5e7eb'
        }}
      >
        <div className="flex items-center justify-center gap-2">
          <button
            onClick={() => setViewMode('day')}
            className={`px-4 py-1.5 rounded-lg font-semibold text-sm transition-all ${
              viewMode === 'day'
                ? 'text-white shadow-lg'
                : 'bg-white text-gray-700 hover:bg-gray-100'
            }`}
            style={viewMode === 'day' ? { backgroundColor: enconOrange } : {}}
          >
            Daily
          </button>
          <button
            onClick={() => setViewMode('week')}
            className={`px-4 py-1.5 rounded-lg font-semibold text-sm transition-all ${
              viewMode === 'week'
                ? 'text-white shadow-lg'
                : 'bg-white text-gray-700 hover:bg-gray-100'
            }`}
            style={viewMode === 'week' ? { backgroundColor: enconOrange } : {}}
          >
            Weekly
          </button>
          <button
            onClick={() => setViewMode('month')}
            className={`px-4 py-1.5 rounded-lg font-semibold text-sm transition-all ${
              viewMode === 'month'
                ? 'text-white shadow-lg'
                : 'bg-white text-gray-700 hover:bg-gray-100'
            }`}
            style={viewMode === 'month' ? { backgroundColor: enconOrange } : {}}
          >
            Monthly
          </button>
        </div>
      </div>

      {/* Main Content - Split Screen with bottom padding for footer */}
      <div className="flex-1 flex flex-col md:flex-row overflow-hidden pb-24">
        {/* LEFT SIDE - Scrollable Areas List */}
        <div className="w-full md:w-1/2 p-6 overflow-y-auto h-full pb-24">
          {/* Areas List - Grouped by Vehicle */}
          <div className="space-y-3">
            <h2 className="text-xl font-bold mb-4 sticky top-0 bg-white py-2 z-10" style={{ color: enconBlack }}>
              🔥 Vehicle Areas v2.0 ({viewMode}) {loading && <span className="text-sm text-gray-500">(Loading...)</span>}
            </h2>
            {areaData.length === 0 ? (
              <div className="text-gray-500 text-center py-8">
                No deliveries scheduled for this period
              </div>
            ) : (
              areaData.map((vehicleData, idx) => {
                // Off-road vehicles or vehicles with no deliveries are shown in red
                const isOffRoadOrNoDeliveries = vehicleData.isOffRoad || !vehicleData.hasDeliveries;
                const vehicleColor = isOffRoadOrNoDeliveries ? '#ef4444' : getVehicleColor(vehicleData.vehicleReg, vehicleData.vehicleType);
                
                return (
                  <div
                    key={idx}
                    className={`p-4 rounded-lg border-2 ${
                      vehicleData.isFull ? 'bg-red-50 opacity-60' : 
                      isOffRoadOrNoDeliveries ? 'bg-red-50' : 'bg-white'
                    }`}
                    style={{ 
                      borderColor: isOffRoadOrNoDeliveries ? '#ef4444' : (vehicleData.isFull ? '#fca5a5' : vehicleColor),
                      borderWidth: '3px'
                    }}
                  >
                    <div className="flex flex-col gap-2">
                      {/* Vehicle Registration with color indicator and color picker */}
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-2">
                          <div 
                            className="w-4 h-4 rounded-full flex-shrink-0" 
                            style={{ backgroundColor: vehicleColor }}
                          />
                          <div className={`font-bold text-lg ${vehicleData.isFull ? 'line-through' : ''}`}
                            style={{ 
                              color: isOffRoadOrNoDeliveries ? '#dc2626' : (
                                vehicleData.isFull ? '#dc2626' : (
                                  // Non-default vehicles in black
                                  !['VX24 ZBW', 'VX24 YXK', 'VX24 ZDT', 'PO23 XYD'].includes(vehicleData.vehicleReg) ? '#000000' : enconBlack
                                )
                              )
                            }}>
                            {vehicleData.vehicleReg}
                          </div>
                        </div>
                        
                        {/* Color picker for non-default vehicles */}
                        {!['VX24 ZBW', 'VX24 YXK', 'VX24 ZDT', 'PO23 XYD'].includes(vehicleData.vehicleReg) && (
                          <input
                            type="color"
                            value={vehicleColor}
                            onChange={(e) => {
                              const newColor = e.target.value;
                              setVehicleColors(prev => ({
                                ...prev,
                                [vehicleData.vehicleReg]: newColor
                              }));
                            }}
                            className="w-8 h-8 rounded border-2 cursor-pointer"
                            style={{ borderColor: enconOrange }}
                            title="Change vehicle color"
                          />
                        )}
                      </div>

                      {/* Off-road or no deliveries message */}
                      {isOffRoadOrNoDeliveries ? (
                        <div className="ml-6 text-sm font-bold text-red-600 uppercase">
                          VEHICLE OFF ROAD
                        </div>
                      ) : (
                        /* Areas list with times */
                        <div className="ml-6 space-y-2">
                          {vehicleData.areas.map((area, areaIdx) => (
                            <div key={areaIdx} className="space-y-1">
                              <div className="text-sm font-medium" style={{ color: vehicleColor }}>
                                • {area.name}
                              </div>
                              {/* Times for this specific area */}
                              {area.times && area.times.length > 0 && (
                                <div className="ml-4 text-xs text-gray-600">
                                  {area.times.length === 1 ? (
                                    `Time: ${area.times[0]}`
                                  ) : (
                                    `Times: ${area.times.join(', ')}`
                                  )}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* RIGHT SIDE - Map */}
        <div className="w-full md:w-1/2 h-96 md:h-full bg-gray-100 relative pb-24" style={{ zIndex: 1 }}>
          {baseLocation ? (
            <MapContainer
              center={[baseLocation.lat, baseLocation.lng]}
              zoom={8}
              style={{ height: '100%', width: '100%', zIndex: 1 }}
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              
              {/* 50 mile radius - Outer circle with lighter grey, larger */}
              <Circle
                center={[baseLocation.lat, baseLocation.lng]}
                radius={80467.2} // 50 miles in meters
                pathOptions={{
                  color: '#9ca3af',
                  fillColor: '#e5e7eb',
                  fillOpacity: 0.2,
                  weight: 3,
                  dashArray: '10, 5'
                }}
              />
              
              {/* 25 mile radius - Inner circle with darker grey */}
              <Circle
                center={[baseLocation.lat, baseLocation.lng]}
                radius={40233.6} // 25 miles in meters
                pathOptions={{
                  color: '#374151',
                  fillColor: '#4b5563',
                  fillOpacity: 0.25,
                  weight: 3
                }}
              />
              
              {/* Postcode markers - Color-coded by vehicle */}
              {postcodeLocations.map((location, idx) => {
                // Get vehicle color or default to orange for off-road/no-delivery vehicles
                const markerColor = getVehicleColor(location.vehicleReg, null);
                
                return (
                  <CircleMarker
                    key={idx}
                    center={[location.lat, location.lng]}
                    radius={6}
                    pathOptions={{
                      color: markerColor,
                      fillColor: markerColor,
                      fillOpacity: 0.8,
                      weight: 2
                    }}
                  >
                    <Popup>
                      <div style={{ minWidth: '200px' }}>
                        <strong style={{ color: markerColor }}>{location.vehicleReg}</strong><br />
                        <strong>{location.area}</strong><br />
                        {location.postcode}<br />
                        {location.customer && <><strong>Customer:</strong> {location.customer}<br /></>}
                        {location.time && <><strong>Time:</strong> {location.time}<br /></>}
                        {location.orderNumber && <><strong>Order:</strong> {location.orderNumber}<br /></>}
                        
                        {/* Cost and Contribution */}
                        <div style={{ marginTop: '8px', paddingTop: '8px', borderTop: '1px solid #e5e7eb' }}>
                          {location.costPerEntry !== undefined && (
                            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px' }}>
                              <span style={{ color: '#dc2626', fontWeight: 'bold' }}>
                                Cost: £{location.costPerEntry.toFixed(2)}
                              </span>
                              <span style={{ color: '#16a34a', fontWeight: 'bold' }}>
                                Cont: £{(location.contribution || 0).toFixed(2)}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    </Popup>
                  </CircleMarker>
                );
              })}
            </MapContainer>
          ) : (
            <div className="h-full flex items-center justify-center text-gray-500">
              <div className="text-center">
                <p className="text-lg font-semibold">Loading map...</p>
                <p className="text-sm">Please set base postcode in settings</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Desktop/iPad Footer ONLY - Hidden on mobile */}
      <div className="hidden md:block fixed bottom-0 left-0 right-0" style={{ zIndex: 9999, paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}>
        <div className="px-2 md:px-6 pb-2 md:pb-3">
          {/* Dashboard Dropdown */}
          <DashboardDropdown 
            isOpen={isDashboardOpen} 
            user={user} 
            onClose={() => setIsDashboardOpen(false)} 
          />
          
          {/* Search Bar Dropdown - positioned above footer */}
          {showSearchBar && (
            <div
              ref={searchBarRef}
              className="absolute bottom-full left-0 right-0 mb-2"
              style={{ zIndex: 9998 }}
            >
              <div
                className="backdrop-blur-md border-t-2 border-b-2 px-4 lg:px-8 py-3 mx-2"
                style={{
                  backgroundColor: 'rgba(128, 128, 128, 0.85)',
                  borderTopColor: enconOrange,
                  borderBottomColor: enconOrange,
                  boxShadow: '0 -4px 6px rgba(0, 0, 0, 0.1)',
                  borderRadius: '8px',
                }}
              >
                <div className="flex items-center gap-3 max-w-3xl mx-auto mb-3">
                  <Search className="w-5 h-5 text-white flex-shrink-0" />
                  <input
                    type="text"
                    placeholder="Search by order number, customer name, or postcode..."
                    value={searchQuery}
                    onChange={(e) => {
                      const query = e.target.value;
                      setSearchQuery(query);
                      if (query.trim()) {
                        debouncedSearch(query);
                      } else {
                        setSearchResults(null);
                        setIsSearching(false);
                      }
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Escape') {
                        setShowSearchBar(false);
                        setSearchQuery('');
                        setSearchResults(null);
                      }
                    }}
                    className="flex-1 px-4 py-2 rounded-lg border-2 border-white/30 bg-white/20 text-white placeholder-white/60 focus:outline-none focus:border-white/60 transition-all"
                  />
                  <button
                    onClick={() => {
                      setShowSearchBar(false);
                      setSearchQuery('');
                      setSearchResults(null);
                    }}
                    className="p-2 rounded-lg hover:bg-white/20 transition-all text-white"
                    title="Close"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>

                {/* Loading */}
                {isSearching && (
                  <div className="text-center py-4">
                    <div className="text-white text-sm">
                      <div className="inline-block animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                      Searching...
                    </div>
                  </div>
                )}

                {/* Results */}
                {!isSearching && searchResults && (
                  <div className="max-h-64 overflow-y-auto">
                    {searchResults.found ? (
                      <div className="space-y-2">
                        <div className="text-white text-sm font-semibold mb-2">
                          Found {searchResults.count} result(s):
                        </div>
                        {searchResults.results.map((result, index) => (
                          <div
                            key={index}
                            className="bg-white/20 hover:bg-white/30 p-3 rounded-lg transition-all cursor-pointer"
                            onClick={() => {
                              setSelectedDate(new Date(result.date));
                              setShowSearchBar(false);
                              setSearchQuery('');
                              setSearchResults(null);
                              toast.success(`Navigated to ${format(new Date(result.date), 'dd MMM yyyy')}`);
                            }}
                          >
                            <div className="flex items-center justify-between text-white">
                              <div className="flex-1">
                                <div className="font-bold">{result.vehicle} - {result.driver}</div>
                                <div className="text-sm opacity-90">
                                  Date: {format(new Date(result.date), 'dd MMM yyyy')} | 
                                  Order: {result.entry?.order_number || 'N/A'} | 
                                  Customer: {result.entry?.customer_name || 'N/A'}
                                </div>
                              </div>
                              <div className="text-xs px-2 py-1 bg-white/20 rounded">
                                Click to view
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-white text-center py-4 opacity-75">
                        {searchResults.message}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
          
          <div className="bg-white border-t-4 flex items-center justify-between px-6 py-3 gap-4 relative shadow-lg" style={{ borderTopColor: '#ee7203' }}>
            {/* Left Section: View Mode Buttons + Date Navigation */}
            <div className="flex gap-6 items-center flex-1">
              {/* View Mode Buttons - Left side */}
              <div className="flex gap-2">
                <button
                  onClick={() => setViewMode('day')}
                  className={`px-4 py-2 rounded-lg font-bold transition-all ${
                    viewMode === 'day' ? 'neuro-orange text-white shadow-lg' : 'luxury-control'
                  }`}
                  style={viewMode !== 'day' ? { color: enconBlack } : {}}
                >
                  Daily
                </button>
                <button
                  onClick={() => setViewMode('week')}
                  className={`px-4 py-2 rounded-lg font-bold transition-all ${
                    viewMode === 'week' ? 'neuro-orange text-white shadow-lg' : 'luxury-control'
                  }`}
                  style={viewMode !== 'week' ? { color: enconBlack } : {}}
                >
                  Weekly
                </button>
                <button
                  onClick={() => setViewMode('month')}
                  className={`px-4 py-2 rounded-lg font-bold transition-all ${
                    viewMode === 'month' ? 'neuro-orange text-white shadow-lg' : 'luxury-control'
                  }`}
                  style={viewMode !== 'month' ? { color: enconBlack } : {}}
                >
                  Monthly
                </button>
              </div>
              
              {/* Date Navigation with Arrows */}
              <div className="flex items-center gap-4">
              <button
                onClick={prevPeriod}
                className="p-2 rounded-full luxury-control"
                style={{ color: enconOrange }}
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              
              {/* Clickable Date - Opens Calendar Modal */}
              <div className="relative">
                <button
                  onClick={() => setShowCalendarModal(true)}
                  className="min-w-[250px] text-center px-4 py-2 rounded-lg luxury-control hover:shadow-lg transition-all"
                >
                  <div className="text-base font-bold" style={{ color: enconBlack }}>
                    {getDateDisplay(true)}
                  </div>
                </button>
              </div>
              
              <button
                onClick={nextPeriod}
                className="p-2 rounded-full luxury-control"
                style={{ color: enconOrange }}
              >
                <ChevronRight className="w-5 h-5" />
              </button>
              </div>
            </div>
            
            {/* Center: Dashboard Button (absolutely positioned) */}
            <div className="absolute left-1/2 transform -translate-x-1/2 z-20">
              <DashboardButton 
                onToggle={() => setIsDashboardOpen(!isDashboardOpen)} 
                isOpen={isDashboardOpen} 
              />
            </div>

            {/* Right: Settings + Search + View Runs Button */}
            <div className="flex items-center gap-3">
              {/* Settings Button */}
              <button
                onClick={() => window.location.href = '/settings'}
                className="p-2.5 rounded-xl luxury-control hover:shadow-lg flex items-center justify-center"
                style={{ color: enconOrange }}
                title="Settings"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 616 0z" />
                </svg>
              </button>
              
              {/* Search Button */}
              <button
                onClick={() => setShowSearchBar(!showSearchBar)}
                className="p-2.5 rounded-xl luxury-control hover:shadow-lg flex items-center justify-center"
                style={{ color: enconOrange }}
                title="Search Orders"
              >
                <Search className="w-6 h-6" />
              </button>
              
              <button
                onClick={() => navigate(-1)}
                className="px-6 py-2 rounded-lg font-bold luxury-control transition-all hover:shadow-lg"
                style={{ color: enconOrange }}
              >
                View Runs
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Calendar Modal */}
      <Dialog open={showCalendarModal} onOpenChange={setShowCalendarModal}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader>
            <DialogTitle style={{ color: enconOrange }}>Select Date</DialogTitle>
          </DialogHeader>
          <div className="p-4">
            <input
              type="date"
              value={format(selectedDate, 'yyyy-MM-dd')}
              onChange={(e) => {
                setSelectedDate(new Date(e.target.value));
                setShowCalendarModal(false);
              }}
              className="w-full p-3 border-2 rounded-lg text-lg"
              style={{ borderColor: enconOrange }}
            />
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Areas;
